import React, { useRef, useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
// @ts-ignore — static cached data from productive-weekday API + contributions API
import ghData from "./githubData.json";

/* ==================== TYPES ==================== */

interface GHPR {
  title: string;
  url: string;
  state: "MERGED" | "OPEN" | "CLOSED";
  repository: { nameWithOwner: string };
}

interface GHContribution {
  date: string;
  count: number;
  level: number;
}

interface RepoGroup {
  repo: string;
  prs: GHPR[];
  merged: number;
}

/* ==================== STATIC DATA (cached, no API calls) ==================== */

const contribPRs: GHPR[] = (ghData as any).userContribPRs || [];
const contributions2020: GHContribution[] = (ghData as any).contributions2020 || [];
const contributions2021: GHContribution[] = (ghData as any).contributions2021 || [];
const total2020: number = (ghData as any).total2020 || 0;
const total2021: number = (ghData as any).total2021 || 0;
const totalStars: number = (ghData as any).totalStars || 0;
const selfPRs: GHPR[] = (ghData as any).userSelfPRs || [];

// All real yearly contributions scraped from github.com/users/jugshaurya/contributions
const yearlyContributions: Record<string, { contributions: GHContribution[]; total: number }> =
  (ghData as any).yearlyContributions || {};
const AVAILABLE_YEARS: number[] = Object.keys(yearlyContributions)
  .map((y) => parseInt(y, 10))
  .filter((y) => !isNaN(y))
  .sort((a, b) => b - a); // newest first
const totalAllYears: number = (ghData as any).totalAllYearsContributions || 0;

const POTION_COLORS = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6", "#1abc9c"];

/* ==================== CONTRIBUTION HEATMAP ==================== */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const Heatmap: React.FC<{ contributions: GHContribution[]; year: number; total: number }> = ({ contributions, year, total }) => {
  const { cells, monthLabels, max } = useMemo(() => {
    const calMap: Record<string, number> = {};
    for (const c of contributions) calMap[c.date] = c.count;

    const days: { x: number; y: number; count: number; date: string }[] = [];
    const months: { label: string; x: number }[] = [];
    const step = 14;
    let lastMonth = -1;
    let dayIndex = 0;
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split("T")[0];
      const count = calMap[key] || 0;
      const week = Math.floor(dayIndex / 7);
      const dow = d.getDay();
      days.push({ x: week * step, y: dow * step, count, date: key });
      if (d.getMonth() !== lastMonth) { lastMonth = d.getMonth(); months.push({ label: MONTHS[d.getMonth()], x: week * step }); }
      dayIndex++;
    }
    const counts = days.map(d => d.count).filter(c => c > 0);
    return { cells: days, monthLabels: months, max: Math.max(8, ...counts) };
  }, [contributions, year]);

  const W = 53 * 14;
  return (
    <HeatmapCard>
      <HeatmapHeader>
        <HeatmapTitle>{year}</HeatmapTitle>
        <HeatmapRight><b>{total.toLocaleString()}</b> contributions</HeatmapRight>
      </HeatmapHeader>
      <HeatmapScroll>
        <HeatmapSvg viewBox={`-5 -5 ${W + 10} 112`}>
          {monthLabels.map((m, i) => (<text key={i} x={m.x} y={-1} fill="#5a5a4a" fontSize="8" fontFamily="JetBrains Mono">{m.label}</text>))}
          {cells.map((c, i) => (
            <rect key={i} x={c.x} y={c.y + 8} width="10" height="10" rx="2"
              fill={c.count === 0 ? "rgba(255,255,255,0.04)" : "#2cbb5d"}
              opacity={c.count === 0 ? 1 : Math.min(1, 0.25 + (c.count / max) * 0.75)}>
              <title>{c.date}: {c.count}</title>
            </rect>
          ))}
        </HeatmapSvg>
      </HeatmapScroll>
    </HeatmapCard>
  );
};

/* ==================== COMPONENT ==================== */

const OpenSource: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [expandedRepo, setExpandedRepo] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(AVAILABLE_YEARS[0] || new Date().getFullYear());
  const selectedYearData = yearlyContributions[String(selectedYear)] || { contributions: [], total: 0 };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Group contrib PRs by repo
  const repoGroups: RepoGroup[] = useMemo(() => {
    const map: Record<string, GHPR[]> = {};
    for (const pr of contribPRs) {
      const repo = pr.repository?.nameWithOwner || "unknown";
      if (!map[repo]) map[repo] = [];
      map[repo].push(pr);
    }
    // Only show RNE and Coding Blocks repos
    const showRepos = ["react-native-elements/playground", "react-native-elements/react-native-elements", "coding-blocks/gondor"];
    return Object.entries(map)
      .filter(([repo]) => showRepos.some(s => repo.includes(s.split("/")[0])))
      .map(([repo, prs]) => ({ repo, prs, merged: prs.filter(p => p.state === "MERGED").length }))
      .sort((a, b) => b.prs.length - a.prs.length);
  }, []);

  const totalPRs = contribPRs.length;
  const totalMerged = contribPRs.filter(p => p.state === "MERGED").length;

  const REPO_COLORS = ["#d4534a", "#2e8b57", "#4169e1", "#d4a843", "#e74c3c", "#8b5cf6", "#00bcd4", "#ff6b00", "#22c55e"];

  return (
    <Section ref={ref} $visible={visible}>
      <MapCorner $pos="top-left" />
      <MapCorner $pos="top-right" />
      <MapCorner $pos="bottom-left" />
      <MapCorner $pos="bottom-right" />

      <HeaderRow>
        {/* Goblet of Fire — left side */}
        <GobletWrapper>
          <GobletSVG viewBox="0 0 100 160" fill="none">
            {/* Base */}
            <ellipse cx="50" cy="148" rx="28" ry="6" fill="#4a3520" />
            <rect x="30" y="140" width="40" height="10" rx="2" fill="#5a4228" />
            <rect x="26" y="144" width="48" height="4" rx="1" fill="#6b5030" />
            {/* Stem */}
            <rect x="44" y="100" width="12" height="42" rx="3" fill="#5a4228" />
            <rect x="42" y="98" width="16" height="6" rx="2" fill="#6b5030" />
            {/* Cup body */}
            <path d="M20 40 Q18 70 24 85 Q30 98 42 100 L58 100 Q70 98 76 85 Q82 70 80 40 Z" fill="#3a2818" />
            <path d="M20 40 Q18 70 24 85 Q30 98 42 100 L58 100 Q70 98 76 85 Q82 70 80 40 Z" fill="url(#cupGrad)" />
            {/* Cup rim */}
            <ellipse cx="50" cy="40" rx="30" ry="8" fill="#7a6040" />
            <ellipse cx="50" cy="40" rx="28" ry="6" fill="#2a1a10" />
            {/* Cup decorative bands */}
            <path d="M22 55 Q50 60 78 55" stroke="#8b6914" strokeWidth="1" fill="none" opacity="0.5" />
            <path d="M24 70 Q50 75 76 70" stroke="#8b6914" strokeWidth="1" fill="none" opacity="0.4" />
            {/* Handles */}
            <path d="M20 50 Q8 55 10 70 Q12 80 22 78" stroke="#5a4228" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M80 50 Q92 55 90 70 Q88 80 78 78" stroke="#5a4228" strokeWidth="3" fill="none" strokeLinecap="round" />
            {/* Big blue fire — outer glow */}
            <g className="goblet-fire">
              {/* Outer flame — wide, soft */}
              <path d="M28 40 Q30 5 38 -8 Q42 -18 50 -25 Q58 -18 62 -8 Q70 5 72 40 Z" fill="#2266cc" opacity="0.25" />
              {/* Main flame */}
              <path d="M30 38 Q34 0 40 -10 Q44 -20 50 -25 Q56 -20 60 -10 Q66 0 70 38 Z" fill="#3388ee" opacity="0.45" />
              {/* Inner flame */}
              <path d="M34 38 Q38 8 43 -4 Q46 -12 50 -18 Q54 -12 57 -4 Q62 8 66 38 Z" fill="#55aaff" opacity="0.55" />
              {/* Core flame */}
              <path d="M38 37 Q42 12 46 2 Q48 -6 50 -12 Q52 -6 54 2 Q58 12 62 37 Z" fill="#88ccff" opacity="0.65" />
              {/* Hot center */}
              <path d="M42 36 Q45 18 48 10 Q49 4 50 -2 Q51 4 52 10 Q55 18 58 36 Z" fill="#bbddff" opacity="0.7" />
              {/* White hot tip */}
              <path d="M46 35 Q48 22 49 16 Q50 8 50 4 Q50 8 51 16 Q52 22 54 35 Z" fill="#ffffff" opacity="0.6" />
            </g>
            {/* Left flame tendril */}
            <g className="goblet-fire-left">
              <path d="M32 38 Q28 20 30 8 Q32 0 36 -5 Q38 5 35 18 Q34 28 35 38 Z" fill="#4488ff" opacity="0.3" />
            </g>
            {/* Right flame tendril */}
            <g className="goblet-fire-right">
              <path d="M68 38 Q72 20 70 8 Q68 0 64 -5 Q62 5 65 18 Q66 28 65 38 Z" fill="#4488ff" opacity="0.3" />
            </g>
            {/* Sparks flying up */}
            <circle cx="35" cy="5" r="2" fill="#88ccff" opacity="0.5" className="goblet-spark" />
            <circle cx="65" cy="0" r="1.5" fill="#aaddff" opacity="0.4" className="goblet-spark" />
            <circle cx="50" cy="-10" r="2" fill="#ffffff" opacity="0.5" className="goblet-spark" />
            <circle cx="42" cy="-5" r="1" fill="#66aaff" opacity="0.6" className="goblet-spark" />
            <circle cx="58" cy="-8" r="1.2" fill="#bbddff" opacity="0.4" className="goblet-spark" />
            <circle cx="30" cy="15" r="1" fill="#4488ff" opacity="0.3" className="goblet-spark" />
            <circle cx="70" cy="12" r="1" fill="#4488ff" opacity="0.3" className="goblet-spark" />
            <defs>
              <linearGradient id="cupGrad" x1="20" y1="40" x2="80" y2="100">
                <stop offset="0%" stopColor="#5a4228" />
                <stop offset="50%" stopColor="#4a3520" />
                <stop offset="100%" stopColor="#3a2818" />
              </linearGradient>
            </defs>
          </GobletSVG>
        </GobletWrapper>

        <Header>
          <Oath>I solemnly swear that I am up to no good</Oath>
          <Title>Open Source Contributions</Title>
          <Subtitle>Navigating unknown codebases & learning tech stacks on the go</Subtitle>
        </Header>

        {/* Vertical year indicator — right side */}
        <YearIndicator>
          <YearText>2020</YearText>
          <YearLine />
          <YearText>2021</YearText>
          <YearLabel>Most Active Years</YearLabel>
        </YearIndicator>
      </HeaderRow>

      {/* Open Source Highlights */}
      <BulletList>
        <Bullet>Contributed <b>10+ merged PRs</b> to React Native Elements — playgrounds, ESLint/Prettier, bug fixes</Bullet>
        <Bullet>Ranked <b>6th in BOSS'20</b> at Coding Blocks — 12 merged PRs across GraphQL, UI & admin features</Bullet>
      </BulletList>

      {/* Potion Flask Stats */}
      <PotionRow>
        {[
          { value: totalPRs, label: "Contribution PRs", color: POTION_COLORS[0] },
          { value: totalMerged, label: "Merged", color: POTION_COLORS[1] },
          { value: selfPRs.length, label: "Self PRs", color: POTION_COLORS[2] },
          { value: totalStars, label: "Stars Earned", color: POTION_COLORS[3] },
          { value: repoGroups.length, label: "Repos", color: POTION_COLORS[4] },
          { value: total2020 + total2021, label: "2020-21 Contribs", color: POTION_COLORS[5] },
        ].map((p, i) => (
          <PotionFlask key={i}>
            <PotionSVG viewBox="0 0 60 90" fill="none">
              {/* Flask neck */}
              <rect x="22" y="2" width="16" height="20" rx="3" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              {/* Cork */}
              <rect x="20" y="0" width="20" height="6" rx="2" fill="#8b6914" />
              {/* Flask body */}
              <path d="M22 22 L12 45 Q8 55 10 65 Q12 78 20 82 Q28 86 30 86 Q32 86 40 82 Q48 78 50 65 Q52 55 48 45 L38 22 Z"
                fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
              {/* Liquid fill — fluctuating */}
              <g className="potion-liquid" style={{ animationDelay: `${i * 0.4}s` }}>
                <path d={`M14 ${75 - Math.min(35, (p.value / 100) * 35)} Q12 78 20 82 Q28 86 30 86 Q32 86 40 82 Q48 78 46 ${75 - Math.min(35, (p.value / 100) * 35)} Z`}
                  fill={p.color} opacity="0.6" />
                <ellipse cx="30" cy={75 - Math.min(35, (p.value / 100) * 35)} rx="16" ry="2"
                  fill={p.color} opacity="0.3" />
              </g>
              {/* Bubbles */}
              <circle cx="25" cy={68 - (i * 3) % 10} r="1.5" fill={p.color} opacity="0.4" />
              <circle cx="33" cy={62 - (i * 5) % 8} r="1" fill={p.color} opacity="0.3" />
              {/* Glass highlight */}
              <path d="M16 35 Q14 50 16 65" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            </PotionSVG>
            <PotionNum style={{ color: p.color }}>{p.value.toLocaleString()}</PotionNum>
            <PotionLabel>{p.label}</PotionLabel>
          </PotionFlask>
        ))}
      </PotionRow>

      {/* Activity Heatmaps */}
      <HeatmapRow>
        <Heatmap contributions={contributions2020} year={2020} total={total2020} />
        <Heatmap contributions={contributions2021} year={2021} total={total2021} />
      </HeatmapRow>

      {/* Footprints */}
      <Trail $visible={visible}>
        {[...Array(12)].map((_, i) => (
          <Footprint key={i} $i={i} $visible={visible}>{"\u{1F43E}"}</Footprint>
        ))}
      </Trail>

      {/* PR Repos Grid */}
      <SectionLabel>Repositories I Contributed To</SectionLabel>
      <Grid>
        {repoGroups.map((group, gi) => {
          const isExpanded = expandedRepo === group.repo;
          const color = REPO_COLORS[gi % REPO_COLORS.length];
          return (
            <RepoCard
              key={group.repo}
              $visible={visible}
              $delay={gi * 0.12}
              $expanded={isExpanded}
              $color={color}
              onClick={() => setExpandedRepo(isExpanded ? null : group.repo)}
            >
              <RepoHeader>
                <RepoIcon $color={color}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill={color}><path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9z"/></svg>
                </RepoIcon>
                <RepoInfo>
                  <RepoName>{group.repo}</RepoName>
                  <RepoMeta>{group.merged} merged / {group.prs.length} total</RepoMeta>
                </RepoInfo>
                <PRCount $color={color}>{group.prs.length}</PRCount>
              </RepoHeader>

              <ProgressBar>
                <ProgressFill $color={color} style={{ width: `${(group.merged / group.prs.length) * 100}%` }} />
              </ProgressBar>

              <PRList $expanded={isExpanded}>
                {group.prs.map((pr, pi) => (
                  <PRItem
                    key={pi}
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    $state={pr.state}
                  >
                    <PRDot $state={pr.state} />
                    <PRContent>
                      <PRTitleText>{pr.title.replace(/^:[a-z_]+:\s*/i, '')}</PRTitleText>
                      <PRURL>{pr.url.replace('https://github.com/', '')}</PRURL>
                    </PRContent>
                    <PRBadge $state={pr.state}>{pr.state === "MERGED" ? "✓" : "○"}</PRBadge>
                  </PRItem>
                ))}
              </PRList>

              <ExpandHint>{isExpanded ? "tap to collapse" : "tap to see all PRs"}</ExpandHint>
            </RepoCard>
          );
        })}
      </Grid>

      {/* All Years GitHub Heatmaps */}
      {AVAILABLE_YEARS.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: "48px" }}>Overall GitHub Activity</SectionLabel>
          <HeatmapRow>
            <YearPicker>
              {AVAILABLE_YEARS.map((y) => (
                <YearTab
                  key={y}
                  $active={y === selectedYear}
                  onClick={() => setSelectedYear(y)}
                >
                  {y}
                  <YearTabTotal>{(yearlyContributions[String(y)]?.total || 0).toLocaleString()}</YearTabTotal>
                </YearTab>
              ))}
            </YearPicker>
            <Heatmap
              contributions={selectedYearData.contributions}
              year={selectedYear}
              total={selectedYearData.total}
            />
            <HeatmapAllTotal>
              <b>{totalAllYears.toLocaleString()}</b> total contributions across {AVAILABLE_YEARS.length} years
            </HeatmapAllTotal>
          </HeatmapRow>
        </>
      )}

      <Footer>
        <Mischief>Mischief Managed.</Mischief>
      </Footer>
    </Section>
  );
};

export default OpenSource;

/* ==================== STYLES ==================== */

const footprintWalk = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.5); }
  30%, 70% { opacity: 0.4; transform: scale(1); }
`;

const Section = styled.section<{ $visible: boolean }>`
  position: relative; max-width: 1000px; margin: 0 auto; padding: 100px 5% 50px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : "30px")});
  transition: opacity 0.8s ease, transform 0.8s ease;
  &::before { content:''; position:absolute; inset:20px; border:1px solid rgba(255,200,100,0.08); border-radius:20px; pointer-events:none; }
`;

const MapCorner = styled.div<{ $pos: string }>`
  position:absolute; width:30px; height:30px; border-color:rgba(255,180,60,0.2); border-style:solid; border-width:0; z-index:2;
  ${({ $pos }) =>
    $pos === "top-left" ? "top:15px;left:15px;border-top-width:2px;border-left-width:2px;border-top-left-radius:8px;" :
    $pos === "top-right" ? "top:15px;right:15px;border-top-width:2px;border-right-width:2px;border-top-right-radius:8px;" :
    $pos === "bottom-left" ? "bottom:15px;left:15px;border-bottom-width:2px;border-left-width:2px;border-bottom-left-radius:8px;" :
    "bottom:15px;right:15px;border-bottom-width:2px;border-right-width:2px;border-bottom-right-radius:8px;"
  }
`;

/* Goblet + Header row */
const fireFlicker = keyframes`
  0% { transform: scaleY(1) scaleX(1) rotate(0deg); }
  15% { transform: scaleY(1.12) scaleX(0.92) rotate(1deg); }
  30% { transform: scaleY(0.88) scaleX(1.08) rotate(-1.5deg); }
  45% { transform: scaleY(1.08) scaleX(0.95) rotate(1deg); }
  60% { transform: scaleY(0.92) scaleX(1.04) rotate(-0.5deg); }
  75% { transform: scaleY(1.1) scaleX(0.96) rotate(1.5deg); }
  90% { transform: scaleY(0.95) scaleX(1.02) rotate(-1deg); }
  100% { transform: scaleY(1) scaleX(1) rotate(0deg); }
`;

const fireSwayLeft = keyframes`
  0%, 100% { transform: rotate(0deg) scaleY(1); }
  30% { transform: rotate(-8deg) scaleY(1.15); }
  70% { transform: rotate(4deg) scaleY(0.9); }
`;

const fireSwayRight = keyframes`
  0%, 100% { transform: rotate(0deg) scaleY(1); }
  30% { transform: rotate(8deg) scaleY(1.15); }
  70% { transform: rotate(-4deg) scaleY(0.9); }
`;

const sparkFloat = keyframes`
  0% { opacity: 0; transform: translateY(0) scale(0.5); }
  30% { opacity: 0.8; transform: translateY(-8px) scale(1); }
  100% { opacity: 0; transform: translateY(-20px) scale(0.3); }
`;

const HeaderRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 30px; margin-bottom: 30px;
  @media(max-width: 700px) { flex-direction: column; gap: 16px; }
`;

const GobletWrapper = styled.div`
  flex-shrink: 0; width: 100px;
  @media(max-width: 700px) { width: 70px; }
`;

const GobletSVG = styled.svg`
  width: 100%; height: auto;
  filter: drop-shadow(0 4px 16px rgba(68, 136, 255, 0.2));

  .goblet-fire { transform-origin: 50% 38px; animation: ${fireFlicker} 1.5s ease-in-out infinite; }
  .goblet-fire-left { transform-origin: 34px 38px; animation: ${fireSwayLeft} 2s ease-in-out infinite; }
  .goblet-fire-right { transform-origin: 66px 38px; animation: ${fireSwayRight} 2s ease-in-out infinite 0.3s; }

  .goblet-spark { animation: ${sparkFloat} 2s ease-out infinite; }
  .goblet-spark:nth-of-type(1) { animation-delay: 0s; }
  .goblet-spark:nth-of-type(2) { animation-delay: 0.3s; }
  .goblet-spark:nth-of-type(3) { animation-delay: 0.6s; }
  .goblet-spark:nth-of-type(4) { animation-delay: 0.9s; }
  .goblet-spark:nth-of-type(5) { animation-delay: 1.2s; }
  .goblet-spark:nth-of-type(6) { animation-delay: 1.5s; }
  .goblet-spark:nth-of-type(7) { animation-delay: 1.8s; }
`;

const Header = styled.div`text-align: center; flex: 1;`;
const Oath = styled.p`font-family:'Cinzel',serif; font-size:0.7rem; font-style:italic; color:#8b6914; letter-spacing:0.15em; margin-bottom:12px; opacity:0.7;`;
const Title = styled.h2`
  font-family:'Cinzel',serif; font-size:clamp(1.6rem,3.5vw,2.4rem); font-weight:800; letter-spacing:0.02em; margin-bottom:6px;
  background:linear-gradient(90deg,#c4a882,#ffd700,#ff8c00,#c4a882); background-size:200% auto;
  -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent;
`;
const Subtitle = styled.p`font-family:'Crimson Text',serif; font-size:0.95rem; font-style:italic; color:#7a6b5d; margin-bottom:0;`;

const YearIndicator = styled.div`
  display:flex; flex-direction:column; align-items:center; gap:6px; flex-shrink:0;
  @media(max-width:700px) { flex-direction:row; gap:10px; }
`;
const YearText = styled.span`
  font-family:'Cinzel',serif; font-size:1.1rem; font-weight:800; color:#ffd700;
  writing-mode:vertical-rl; text-orientation:mixed; letter-spacing:0.1em;
  @media(max-width:700px) { writing-mode:horizontal-tb; font-size:0.9rem; }
`;
const YearLine = styled.div`
  width:2px; height:20px; background:linear-gradient(180deg,#ffd700,rgba(255,215,0,0.2)); border-radius:1px;
  @media(max-width:700px) { width:20px; height:2px; background:linear-gradient(90deg,#ffd700,rgba(255,215,0,0.2)); }
`;
const YearLabel = styled.span`
  font-family:'JetBrains Mono',monospace; font-size:0.4rem; color:#5a4a3a; letter-spacing:0.1em;
  text-transform:uppercase; writing-mode:vertical-rl; text-orientation:mixed;
  @media(max-width:700px) { writing-mode:horizontal-tb; }
`;

/* Bullet list with footstep icons */
const stepFade = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.8; }
`;

const BulletList = styled.ul`
  list-style: none; padding: 0; margin: 0 auto 28px; max-width: 650px;
  display: flex; flex-direction: column; gap: 10px;
`;

const Bullet = styled.li`
  font-family: 'Crimson Text', serif; font-size: 0.95rem; color: #9a8a7a; line-height: 1.7;
  display: flex; align-items: flex-start; gap: 10px;
  b { color: #c4a882; font-weight: 600; }

  &::before {
    content: "🐾";
    font-size: 0.75rem;
    flex-shrink: 0;
    margin-top: 3px;
    filter: sepia(1) saturate(0.4);
    animation: ${stepFade} 2s ease-in-out infinite;
  }

  &:nth-child(2)::before { animation-delay: 0.6s; }
  &:nth-child(3)::before { animation-delay: 1.2s; }
`;


/* Potion flasks */
const potionBubble = keyframes`
  0%,100% { transform:translateY(0); opacity:0.3; }
  50% { transform:translateY(-4px); opacity:0.6; }
`;

const liquidWave = keyframes`
  0% { transform:translateY(0); }
  25% { transform:translateY(-3px); }
  50% { transform:translateY(1px); }
  75% { transform:translateY(-2px); }
  100% { transform:translateY(0); }
`;

const PotionRow = styled.div`
  display:flex; justify-content:center; gap:20px; flex-wrap:wrap; margin-bottom:28px;
  @media(max-width:600px) { gap:12px; }
`;

const PotionFlask = styled.div`
  display:flex; flex-direction:column; align-items:center; gap:4px; transition:transform 0.3s;
  &:hover { transform:translateY(-4px); }
  &:hover circle { animation:${potionBubble} 1s ease-in-out infinite; }
  .potion-liquid { animation:${liquidWave} 3s ease-in-out infinite; }
`;

const PotionSVG = styled.svg`width:50px; height:75px; filter:drop-shadow(0 2px 8px rgba(0,0,0,0.3));`;
const PotionNum = styled.span`font-family:'Cinzel',serif; font-size:0.85rem; font-weight:800;`;
const PotionLabel = styled.span`font-family:'JetBrains Mono',monospace; font-size:0.48rem; color:#6a5a4a; letter-spacing:0.06em; text-transform:uppercase; text-align:center; max-width:80px;`;

/* Heatmaps */
const HeatmapRow = styled.div`display:flex; flex-direction:column; gap:14px; margin-bottom:28px; align-items:center;`;
const HeatmapCard = styled.div`width:100%; max-width:850px; padding:1rem 1.2rem; background:rgba(15,12,25,0.5); border:1px solid rgba(255,200,100,0.06); border-radius:10px;`;
const HeatmapHeader = styled.div`display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; flex-wrap:wrap; gap:8px;`;
const HeatmapTitle = styled.h3`font-family:'Cinzel',serif; font-size:0.8rem; font-weight:700; color:#e8dfd5;`;
const HeatmapRight = styled.span`font-family:'JetBrains Mono',monospace; font-size:0.55rem; color:#7a6b5d; b{color:#ffd700;}`;
const HeatmapScroll = styled.div`overflow-x:auto; -webkit-overflow-scrolling:touch;`;
const HeatmapSvg = styled.svg`width:100%; min-width:680px; height:auto;`;

/* Trail */
const Trail = styled.div<{ $visible: boolean }>`display:flex; justify-content:center; gap:18px; margin-bottom:24px; opacity:${({ $visible }) => $visible ? 1 : 0}; transition:opacity 1s ease 0.5s;`;
const Footprint = styled.span<{ $i: number; $visible: boolean }>`
  font-size:0.7rem; opacity:0; transform:rotate(${({ $i }) => $i % 2 === 0 ? '-15deg' : '15deg'});
  filter:sepia(1) saturate(0.3); animation:${({ $visible }) => $visible ? footprintWalk : 'none'} 2s ease infinite;
  animation-delay:${({ $i }) => $i * 0.15}s;
`;

/* Section label */
const SectionLabel = styled.h3`
  font-family:'Cinzel',serif; font-size:0.85rem; font-weight:700; color:#c4a882; letter-spacing:0.08em; margin-bottom:16px; text-align:center;
`;

/* Grid */
const Grid = styled.div`display:grid; grid-template-columns:repeat(2,1fr); gap:16px; align-items:start; @media(max-width:700px){grid-template-columns:1fr;}`;

const RepoCard = styled.div<{ $visible: boolean; $delay: number; $expanded: boolean; $color: string }>`
  padding:20px 18px; border-radius:14px; background:rgba(20,12,30,0.6); border:1px solid rgba(255,180,60,0.08);
  border-top:2px solid ${({ $color }) => $color}60; cursor:pointer; transition:all 0.4s ease;
  opacity:${({ $visible }) => $visible ? 1 : 0}; transform:translateY(${({ $visible }) => $visible ? 0 : "20px"});
  transition-delay:${({ $delay }) => $delay}s;
  &:hover { border-color:${({ $color }) => $color}40; background:rgba(25,15,40,0.7); transform:translateY(-2px); }
`;

const RepoHeader = styled.div`display:flex; align-items:center; gap:12px; margin-bottom:10px;`;
const RepoIcon = styled.div<{ $color: string }>`width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; background:${({ $color }) => $color}15; border:1px solid ${({ $color }) => $color}25;`;
const RepoInfo = styled.div`flex:1; min-width:0;`;
const RepoName = styled.h3`font-family:'JetBrains Mono',monospace; font-size:0.82rem; font-weight:700; color:#e8dfd5; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;`;
const RepoMeta = styled.span`font-family:'JetBrains Mono',monospace; font-size:0.42rem; color:#5a4a3a;`;
const PRCount = styled.span<{ $color: string }>`font-family:'JetBrains Mono',monospace; font-size:0.7rem; font-weight:800; color:${({ $color }) => $color}; background:${({ $color }) => $color}12; padding:3px 10px; border-radius:20px; border:1px solid ${({ $color }) => $color}20;`;
const ProgressBar = styled.div`width:100%; height:3px; background:rgba(255,255,255,0.04); border-radius:2px; overflow:hidden; margin-bottom:8px;`;
const ProgressFill = styled.div<{ $color: string }>`height:100%; border-radius:2px; transition:width 1s ease; background:linear-gradient(90deg,${({ $color }) => $color}88,${({ $color }) => $color});`;

const PRList = styled.div<{ $expanded: boolean }>`max-height:${({ $expanded }) => $expanded ? "800px" : "0"}; overflow:hidden; transition:max-height 0.5s cubic-bezier(0.4,0,0.2,1);`;

const PRItem = styled.a<{ $state: string }>`
  display:flex; align-items:flex-start; gap:10px; padding:8px 0; border-bottom:1px solid rgba(255,255,255,0.03);
  text-decoration:none; cursor:pointer; transition:background 0.2s, padding 0.2s;
  &:hover { background:rgba(255,255,255,0.03); border-radius:6px; padding-left:6px; padding-right:6px; }
  &:last-child { border-bottom:none; }
`;
const PRDot = styled.div<{ $state: string }>`width:6px; height:6px; border-radius:50%; margin-top:5px; flex-shrink:0; background:${({ $state }) => $state === "MERGED" ? "#9b6dff" : "#4ade80"}; box-shadow:0 0 6px ${({ $state }) => $state === "MERGED" ? "rgba(155,109,255,0.4)" : "rgba(74,222,128,0.4)"};`;
const PRContent = styled.div`flex:1; min-width:0;`;
const PRTitleText = styled.p`font-family:'Raleway',sans-serif; font-size:0.75rem; font-weight:600; color:#d0c8c0; line-height:1.5; margin:0 0 2px; transition:color 0.2s; ${PRItem}:hover &{color:#ffd700;}`;
const PRURL = styled.p`font-family:'JetBrains Mono',monospace; font-size:0.4rem; color:#4a3a2a; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; ${PRItem}:hover &{color:#6a5a4a;}`;
const PRBadge = styled.span<{ $state: string }>`font-size:0.6rem; font-weight:700; flex-shrink:0; margin-top:2px; color:${({ $state }) => $state === "MERGED" ? "#9b6dff" : "#4ade80"};`;
const ExpandHint = styled.p`font-family:'JetBrains Mono',monospace; font-size:0.42rem; color:#5a4a3a; text-align:center; margin-top:6px; letter-spacing:0.1em; transition:color 0.3s; ${RepoCard}:hover &{color:#8a7a6a;}`;

const Footer = styled.div`text-align:center; margin-top:40px; padding-top:20px;`;
const Mischief = styled.p`font-family:'Cinzel',serif; font-size:0.75rem; font-style:italic; color:#8b6914; letter-spacing:0.1em; opacity:0.6;`;

const YearPicker = styled.div`
  display:flex; flex-wrap:wrap; justify-content:center; gap:6px;
  padding:6px 8px; border-radius:12px;
  background:rgba(15,12,25,0.4); border:1px solid rgba(255,200,100,0.06);
  max-width:850px; width:100%;
`;
const YearTab = styled.button<{ $active: boolean }>`
  display:flex; flex-direction:column; align-items:center; gap:2px;
  padding:6px 12px; border-radius:8px; cursor:pointer;
  font-family:'Cinzel',serif; font-size:0.75rem; font-weight:700;
  border:1px solid ${({ $active }) => ($active ? "rgba(255,215,0,0.45)" : "rgba(255,255,255,0.06)")};
  background:${({ $active }) => ($active ? "rgba(255,215,0,0.08)" : "transparent")};
  color:${({ $active }) => ($active ? "#ffd700" : "#9a8a7a")};
  transition:all 0.25s ease;
  &:hover { color:#ffd700; border-color:rgba(255,215,0,0.3); background:rgba(255,215,0,0.05); }
`;
const YearTabTotal = styled.span`
  font-family:'JetBrains Mono',monospace; font-size:0.48rem;
  color:#7a6b5d; letter-spacing:0.05em; text-transform:uppercase;
`;
const HeatmapAllTotal = styled.div`
  font-family:'JetBrains Mono',monospace; font-size:0.6rem;
  color:#7a6b5d; letter-spacing:0.06em; text-transform:uppercase;
  text-align:center; margin-top:4px;
  b { color:#ffd700; font-size:0.8rem; font-family:'Cinzel',serif; letter-spacing:0.02em; }
`;
